import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import LinkExtension from "@tiptap/extension-link";
import { Markdown } from "tiptap-markdown";
import { Bold, Italic, Link, Heading3, List, Undo, Redo, ExternalLink, Pencil, Unlink, X, Check, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect, useCallback } from "react";

interface TiptapEditorProps {
  content: string;
  onChange: (markdown: string) => void;
}

export function TiptapEditor({ content, onChange }: TiptapEditorProps) {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [showSource, setShowSource] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3] },
      }),
      LinkExtension.configure({
        openOnClick: false,
        autolink: true,
      }),
      // breaks: true — parse single newlines in saved markdown as line breaks,
      // matching how the public Updates page renders them (remark-breaks).
      Markdown.configure({ breaks: true }),
    ],
    content,
    editorProps: {
      handleClick: (_view, _pos, event) => {
        if ((event.target as HTMLElement).closest("a")) {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.storage.markdown.getMarkdown());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.storage.markdown.getMarkdown();
    if (current !== content) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  const addLink = useCallback(() => {
    if (!editor || !newLinkUrl.trim()) {
      setShowLinkInput(false);
      return;
    }
    const href = /^https?:\/\//.test(newLinkUrl) ? newLinkUrl : `https://${newLinkUrl}`;
    editor.chain().focus().setLink({ href }).run();
    setShowLinkInput(false);
    setNewLinkUrl("");
  }, [editor, newLinkUrl]);

  if (!editor) return null;

  const btnClass = (active: boolean) =>
    `h-7 w-7 p-0 ${active ? "bg-muted text-foreground" : "text-muted-foreground"}`;

  return (
    <div className="rounded-md border border-input overflow-hidden">
      {/* Toolbar */}
      <div className="border-b border-input px-2 py-1.5 flex items-center gap-1 bg-muted/30">
        <Button type="button" variant="outline" size="sm" className={btnClass(editor.isActive("bold"))} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
          <Bold className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" variant="outline" size="sm" className={btnClass(editor.isActive("italic"))} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
          <Italic className="w-3.5 h-3.5" />
        </Button>
        <Button
          type="button" variant="outline" size="sm"
          className={btnClass(editor.isActive("link") || showLinkInput)}
          onClick={() => {
            if (editor.isActive("link")) return; // bubble menu handles existing links
            setNewLinkUrl("");
            setShowLinkInput(!showLinkInput);
          }}
          title="Link"
        >
          <Link className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" variant="outline" size="sm" className={btnClass(editor.isActive("heading", { level: 3 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading">
          <Heading3 className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" variant="outline" size="sm" className={btnClass(editor.isActive("bulletList"))} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">
          <List className="w-3.5 h-3.5" />
        </Button>
        <div className="ml-auto flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" className={`h-7 w-7 p-0 ${showSource ? "text-foreground" : "text-muted-foreground"}`} onClick={() => setShowSource(!showSource)} title="View Markdown source">
            <Code className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
            <Undo className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
            <Redo className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Inline link input bar (for adding new links) */}
      {showLinkInput && (
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-input bg-muted/20">
          <Link className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          <Input
            type="url"
            placeholder="Paste or type URL..."
            value={newLinkUrl}
            onChange={(e) => setNewLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); addLink(); }
              if (e.key === "Escape") { setShowLinkInput(false); editor.chain().focus().run(); }
            }}
            className="h-7 flex-1 text-xs border-0 shadow-none focus-visible:ring-0 bg-transparent"
            autoFocus
          />
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={addLink} title="Apply">
            <Check className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setShowLinkInput(false); editor.chain().focus().run(); }} title="Cancel">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {/* Link bubble menu (appears when clicking existing links) */}
      {!showSource && <LinkBubbleMenu editor={editor} />}

      {/* Editor content or source view */}
      {showSource ? (
        <Textarea
          value={editor.storage.markdown.getMarkdown()}
          onChange={(e) => {
            onChange(e.target.value);
            editor.commands.setContent(e.target.value);
          }}
          className="font-mono text-xs border-0 rounded-none shadow-none focus-visible:ring-0 min-h-[280px] max-h-[400px] resize-none"
        />
      ) : (
        <EditorContent
          editor={editor}
          className="prose prose-sm prose-slate max-w-none px-3 py-2 min-h-[280px] max-h-[400px] overflow-y-auto [&_.tiptap]:outline-none [&_.tiptap]:border-none [&_.ProseMirror]:outline-none [&_.ProseMirror:focus]:outline-none [&_.ProseMirror:focus-visible]:outline-none [&_.ProseMirror:focus-visible]:ring-0 [&_.ProseMirror:focus-visible]:shadow-none [&_*:focus]:outline-none [&_*:focus-visible]:outline-none [&_*:focus-visible]:ring-0 prose-headings:text-foreground prose-headings:text-base prose-headings:font-semibold prose-li:my-1 [&_li>p]:my-0 [&_li>p+p]:mt-2 prose-p:text-foreground/80 prose-li:text-foreground/80 prose-strong:text-foreground prose-em:text-foreground/70 prose-hr:border-border/60 prose-a:text-primary prose-a:underline"
        />
      )}
    </div>
  );
}

function LinkBubbleMenu({ editor }: { editor: NonNullable<ReturnType<typeof useEditor>> }) {
  const [isEditing, setIsEditing] = useState(false);
  const [url, setUrl] = useState("");

  const saveLink = useCallback(() => {
    if (!url.trim()) {
      editor.chain().focus().unsetLink().run();
    } else {
      const href = /^https?:\/\//.test(url) ? url : `https://${url}`;
      editor.chain().focus().setLink({ href }).run();
    }
    setIsEditing(false);
  }, [editor, url]);

  const href = editor.getAttributes("link").href || "";

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="linkBubbleMenu"
      shouldShow={({ editor }) => editor.isActive("link")}
      options={{ placement: "bottom-start" }}
    >
      <div className="flex items-center gap-1 rounded-lg border bg-popover p-1.5 shadow-md text-popover-foreground">
        {isEditing ? (
          <>
            <Input
              type="url"
              placeholder="https://..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); saveLink(); }
                if (e.key === "Escape") { setIsEditing(false); }
              }}
              className="h-7 w-52 text-xs"
              autoFocus
            />
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={saveLink} title="Save">
              <Check className="w-3.5 h-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setIsEditing(false)} title="Cancel">
              <X className="w-3.5 h-3.5" />
            </Button>
          </>
        ) : (
          <>
            <span className="max-w-[200px] truncate text-xs text-primary px-1.5 py-0.5">
              {href}
            </span>
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => window.open(href, "_blank")} title="Open link">
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setUrl(href); setIsEditing(true); }} title="Edit link">
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => editor.chain().focus().unsetLink().run()} title="Remove link">
              <Unlink className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
      </div>
    </BubbleMenu>
  );
}
